import { Injectable } from '@angular/core';
import {
  GestaoPatioResumoModel,
  GestaoPatioVeiculoModel,
  GestaoPatioVeiculosPaginadosModel
} from '../../app/models/GestaoPatioModel';
import { HttpProvider } from '../http/http';

@Injectable()
export class GestaoPatioProvider {

  constructor(private httpSrv: HttpProvider) {
  }

  async getResumo(codConcessionaria: number): Promise<GestaoPatioResumoModel> {
    const result = await this.httpSrv.get(
      'gestao_patio/concessionaria/' + codConcessionaria + '/visao_geral',
      0
    );

    if (!result.success || !result.data) {
      return this.resumoVazio();
    }

    return {
      vencidas: Number(result.data.vencidas) || 0,
      aVencer: Number(result.data.aVencer) || 0,
      emDia: Number(result.data.emDia) || 0
    };
  }

  private resumoVazio(): GestaoPatioResumoModel {
    return {
      vencidas: 0,
      aVencer: 0,
      emDia: 0
    };
  }

  async getVeiculos(codConcessionaria: number, page: number = 1): Promise<GestaoPatioVeiculosPaginadosModel> {
    const result = await this.httpSrv.get(
      'gestao_patio/concessionaria/' + codConcessionaria + '/veiculos?page=' + page,
      0
    );

    if (!result.success || !result.data) {
      return this.paginacaoVazia();
    }

    return this.normalizarPaginacao(result.data);
  }

  async getVeiculosConcluidos(codConcessionaria: number, page: number = 1): Promise<GestaoPatioVeiculosPaginadosModel> {
    const result = await this.httpSrv.get(
      'gestao_patio/concessionaria/' + codConcessionaria + '/veiculos/concluidos?page=' + page,
      0
    );

    if (!result.success || !result.data) {
      return this.paginacaoVazia();
    }

    return this.normalizarPaginacao(result.data);
  }

  private normalizarPaginacao(data: any): GestaoPatioVeiculosPaginadosModel {
    return {
      current_page: Number(data.current_page) || 1,
      data: Array.isArray(data.data) ? data.data.map(veiculo => this.normalizarVeiculo(veiculo)) : [],
      last_page: Number(data.last_page) || 1,
      next_page_url: data.next_page_url || '',
      per_page: Number(data.per_page) || 15,
      total: Number(data.total) || 0
    };
  }

  private normalizarVeiculo(veiculo: any): GestaoPatioVeiculoModel {
    const veiculoNormalizado = {
      chassi: veiculo && veiculo.chassi ? veiculo.chassi : '',
      modelo: veiculo && veiculo.modelo ? veiculo.modelo : '-',
      cor: veiculo && veiculo.cor ? veiculo.cor : '-',
      dataEntrada: veiculo && veiculo.dataEntrada ? veiculo.dataEntrada : '',
      ultimaInspecao: veiculo && veiculo.ultimaInspecao ? veiculo.ultimaInspecao : '',
      tipo: veiculo && veiculo.tipo ? veiculo.tipo : '-',
      responsavel: veiculo && veiculo.responsavel ? veiculo.responsavel : '-',
      etapas: {
        total: veiculo && veiculo.etapas ? Number(veiculo.etapas.total) || 0 : 0,
        concluidas: veiculo && veiculo.etapas && Array.isArray(veiculo.etapas.concluidas)
          ? veiculo.etapas.concluidas
          : [],
        concluidasDetalhe: veiculo && veiculo.etapas && Array.isArray(veiculo.etapas.concluidasDetalhe)
          ? veiculo.etapas.concluidasDetalhe
          : [],
        urlallchecklists: veiculo && veiculo.etapas && veiculo.etapas.urlallchecklists
          ? veiculo.etapas.urlallchecklists
          : ''
      },
      prazos: {
        tipo: veiculo && veiculo.prazos && veiculo.prazos.tipo ? veiculo.prazos.tipo : 'mensal',
        situacao: this.normalizarSituacao(veiculo && veiculo.prazos ? veiculo.prazos.situacao : ''),
        dias: veiculo && veiculo.prazos ? Number(veiculo.prazos.dias) || 0 : 0,
        obs1: veiculo && veiculo.prazos && veiculo.prazos.obs1 ? veiculo.prazos.obs1 : '',
        obs2: veiculo && veiculo.prazos && veiculo.prazos.obs2 ? veiculo.prazos.obs2 : '',
        motivo: veiculo && veiculo.prazos && veiculo.prazos.motivo ? veiculo.prazos.motivo : '',
        observacao: veiculo && veiculo.prazos && veiculo.prazos.observacao ? veiculo.prazos.observacao : ''
      }
    };

    (<any>veiculoNormalizado).finalizadoManual = veiculo ? veiculo.finalizadoManual : null;
    (<any>veiculoNormalizado).finalizadoManualmente = veiculo ? veiculo.finalizadoManualmente : null;
    (<any>veiculoNormalizado).finalizado_manual = veiculo ? veiculo.finalizado_manual : null;
    (<any>veiculoNormalizado).manual = veiculo ? veiculo.manual : null;
    (<any>veiculoNormalizado.prazos).finalizadoManual = veiculo && veiculo.prazos ? veiculo.prazos.finalizadoManual : null;
    (<any>veiculoNormalizado.prazos).finalizadoManualmente = veiculo && veiculo.prazos ? veiculo.prazos.finalizadoManualmente : null;
    (<any>veiculoNormalizado.prazos).finalizado_manual = veiculo && veiculo.prazos ? veiculo.prazos.finalizado_manual : null;
    (<any>veiculoNormalizado.prazos).manual = veiculo && veiculo.prazos ? veiculo.prazos.manual : null;

    return veiculoNormalizado;
  }

  private normalizarSituacao(situacao: any): string {
    const valor = (situacao || '').toString().toLowerCase().replace(/[\s_\-]/g, '');

    if (valor === 'vencido' || valor === 'vencida' || valor === 'vencidos' || valor === 'vencidas') {
      return 'vencido';
    }

    if (valor === 'avencer' || valor === 'vence' || valor === 'vencer') {
      return 'aVencer';
    }

    if (valor === 'emdia') {
      return 'emDia';
    }

    return situacao || '';
  }

  private paginacaoVazia(): GestaoPatioVeiculosPaginadosModel {
    return {
      current_page: 1,
      data: [],
      last_page: 1,
      next_page_url: '',
      per_page: 15,
      total: 0
    };
  }
}



// WEBPACK FOOTER //
// ./src/providers/gestao-patio/gestao-patio.ts